import { useState, useEffect } from "react";
import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { ChipsInput, MultilingualRichTextEditor } from 'app/components';
import { uploadWithTrackingForContentCover, uploadWithTrackingForContentEditor } from "app/utils/uploadFile";
import { useSnackbar } from "notistack";
import { Autocomplete, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormHelperText, IconButton, Tab, Tabs, TextField } from "@mui/material";
import styled from "@emotion/styled";
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { getAuthors } from "app/services/AuthorService";
import { getCategories } from "app/services/CategoryService";
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DeleteIcon from "@mui/icons-material/Delete";
import config from "config";
import { getFunctions, httpsCallable } from "firebase/functions";
import { fetchContentFromFirestore } from "app/services/ContentService";
import LogService from "app/services/LogService.ts";
import MediaPreviewAndSubtitle from "./video/MediaPreviewAndSubtitle";
const functions = getFunctions();
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const Fieldd = styled(Field)(() => ({
    width: "100%",
    marginBottom: "16px"
}));

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pl: 3, pt: 1, width: "100%" }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

export default function ContentCreationStepper({ open, onClose, onContinue, onContentUpdated, mediaUploadData, contentType, contentId }) {
    const [isEditing, setIsEditing] = React.useState(false);
    const [activeStep, setActiveStep] = React.useState(0);
    const [coverImagePreviewUrl, setCoverImagePreviewUrl] = useState("");
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [initialDescriptions, setInitialDescriptions] = useState(null);
    const [languageTabValue, setLanguageTabValue] = useState(0); // Aktif sekme (0 = TR, 1 = EN)
    const [authors, setAuthors] = useState([]);
    const [categories, setCategories] = useState([]);
    const MAX_FILE_SIZE = 500 * 1024;           // 500 KB , byte cinsinden
    const ASPECT_RATIO = 16 / 9;
    const RATIO_TOLERANCE = 0.05;
    const [coverImageError, setCoverImageError] = useState("");  // imageError state'i
    const [storedCoverImage, setStoredCoverImage] = useState(null);
    const [activeLang, setActiveLang] = useState("tr");

    const isMediaType = contentType === "video" || contentType === "podcast";

    // steps dizisini dinamik oluştur
    const steps = isMediaType
        ? ["Basic Info", "Description", "Media & Subtitles", "Review & Approval"]
        : ["Basic Info", "Description", "Review & Approval"];

    const defaultFormData = {
        title: { en: '', tr: '' },
        summary: { en: '', tr: '' },
        author: '',
        category: '',
        coverImage: '',
        ageGroups: [],
        contentType: '',
        state: 'Draft',
        tags: [],
        description: {},
        durations: {},
        fileSizes: {},
        mediaURLs: {},
        subtitles: []
    };

    const languages = [
        { code: 'tr', label: 'TR', title: 'Turkish' },
        { code: 'en', label: 'EN', title: 'English' },
    ];

    const loadAuthors = async () => {
        setLoading(true);
        try {
            const authorsList = await getAuthors(); // getAuthors metodunu çağır
            setAuthors(authorsList); // Yazarları state'e ata
        } catch (error) {
            console.error("An error occurred while retrieving authors:", error);
            await LogService.addErrorLog("Authors fetch failed", error, {
                step: "loadAuthors",
            });
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        setLoading(true);
        try {
            const categoryList = await getCategories(); // getCategories metodunu çağır
            setCategories(categoryList); // Kategorileri state'e ata
        } catch (error) {
            console.error("An error occurred while retrieving categories:", error);
            await LogService.addErrorLog("Categories fetch failed", error, {
                step: "loadCategories",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAuthors(); // Component mount edildiğinde yazarları yükle
        loadCategories();
    }, []);

    const handleTabChange = (event, newValue) => {
        setLanguageTabValue(newValue);
    };

    // State for form fields
    const [formData, setFormData] = useState(defaultFormData);

    const validationSchema = [
        Yup.object().shape({
            title: Yup.object(
                languages.reduce((acc, lang) => {
                    acc[lang.code] = Yup.string().required(`Title is required in ${lang.title}`);
                    return acc;
                }, {})
            ),
            summary: Yup.object(
                languages.reduce((acc, lang) => {
                    acc[lang.code] = Yup.string().required(`Summary is required in ${lang.title}`);
                    return acc;
                }, {})
            ),
            category: Yup.string().required("This field is required"),
            // tags: Yup.array().min(1, "At least one tag must be entered.").required(),
            contentType: Yup.string().required("Content type must be selected."),
            coverImage: Yup.string().required("This field is required")
        }),
        Yup.object().shape({
            description: Yup.object(
                languages.reduce((acc, lang) => {
                    acc[lang.code] = Yup.string().required(`This field is required in ${lang.title}`);
                    return acc;
                }, {})
            ),
        })
    ];

    useEffect(() => {
        const fetchContentData = async () => {
            try {
                if (contentId) {
                    const contentData = await fetchContentFromFirestore(contentId);
                    if (!contentData) return;
                    const { details, ...content } = contentData;
                    setInitialDescriptions(details.descriptions);
                    setFormData({
                        title: content.title,
                        summary: content.summary,
                        author: content.author,
                        category: content.category,
                        ageGroups: !content.ageGroups ? [] : content.ageGroups,
                        coverImage: content.coverImage,
                        contentType: content.type,
                        state: content.state,
                        tags: content.tags || [],
                        description: details.descriptions,
                        durations: content.durations,
                        fileSizes: content.fileSizes || {},
                        mediaURLs: details.mediaURLs,
                        subtitles: details.subtitles
                        //mediaURl set işlemi gelmesi lazım
                    });
                    setCoverImagePreviewUrl(`${config.IMGIX_BASE_URL}/${content.coverImage}?w=400&auto=format`);
                    setStoredCoverImage(content.coverImage);
                    setIsEditing(true);
                    await LogService.addInfoLog("Content edit opened", {
                        contentId
                    });
                } else {
                    setFormData(prevData => ({
                        ...prevData,
                        contentType: contentType, // parametre gelen type set ediliyor
                        mediaURLs: mediaUploadData?.mediaData?.mediaURLs, // parametre gelen mediaURLs set ediliyor
                        subtitles: mediaUploadData?.mediaData?.subtitles || {},
                        fileSizes: mediaUploadData?.mediaData?.fileSizes || {},
                        durations: mediaUploadData?.mediaData?.durations
                    }));
                }
            }
            catch (error) {
                console.error('Error fetching data:', error);
                await LogService.addErrorLog("Content fetch failed", error, { contentId });
            }
        };

        clearForm();
        setActiveStep(0);
        fetchContentData();
    }, [contentId, contentType, mediaUploadData]);

    const handleRemoveImage = (setFieldValue, field) => (event) => {
        setCoverImagePreviewUrl("");
        setFieldValue(field, "");
    };

    useEffect(() => {
        return () => {
            if (coverImagePreviewUrl) URL.revokeObjectURL(coverImagePreviewUrl);
        };
    }, [coverImagePreviewUrl]);

    const handleFileChange =
        (setFieldValue, setFieldError, setTouched, field) => async (event) => {
            const file = event.target.files[0];
            if (!file) return;

            // 1️⃣ Format kontrolü
            const allowedFormats = ["image/jpeg", "image/jpg", "image/webp"];
            if (!allowedFormats.includes(file.type)) {
                setTouched({ coverImage: true });
                setCoverImageError(
                    "Invalid file format. Please upload a JPEG, JPG, or WEBP image."
                );
                return;
            }

            // 2️⃣ Boyut kontrolü
            if (file.size > MAX_FILE_SIZE) {
                setTouched({ coverImage: true });
                setCoverImageError("The file size must not exceed 500 KB.");
                return;
            }

            // 3️⃣ Görsel ölçü kontrolü
            const objectUrl = URL.createObjectURL(file);
            const img = new Image();
            img.onload = async () => {
                const { width, height } = img;
                const ratio = width / height;
                if (Math.abs(ratio - ASPECT_RATIO) > RATIO_TOLERANCE) {
                    setTouched({ coverImage: true });
                    setCoverImageError(
                        "The image should have an aspect ratio close to 16:9."
                    );
                    URL.revokeObjectURL(objectUrl);
                    return;
                }

                // ✅ Hata yoksa: sadece local state güncelle
                setCoverImageError("");
                setTouched({ coverImage: false });
                setCoverImagePreviewUrl(objectUrl); // sadece preview için
                setFieldValue(field, file); // file objesini form state’e kaydet

                // URL.revokeObjectURL(objectUrl); // memory leak engelle
            };

            img.onerror = () => {
                setTouched({ coverImage: false });
                setFieldError(
                    field,
                    "The image could not be uploaded or is in an unsupported format."
                );
                setCoverImageError(
                    "The image could not be uploaded or is in an unsupported format."
                );
                URL.revokeObjectURL(objectUrl);
            };

            img.src = objectUrl;
        };

    const calculateReadingTime = (editorContent) => {
        const text = editorContent.replace(/<[^>]*>/g, " "); // Tagleri boşlukla değiştir
        const words = text.trim().split(/\s+/).filter(Boolean);
        const wordCount = words.length;

        const wordsPerMinute = 200; // Ortalama okuma hızı
        const durationInSeconds = Math.ceil((wordCount / wordsPerMinute) * 60);

        return durationInSeconds;
    };

    const handleCustomChange = (setFieldValue, field) => (value) => {
        setFieldValue(field, value);
    };

    const handleDescriptionChange = (setFieldValue, field) => (value, langCode) => {
        setFieldValue(`${field}.${langCode}`, value);
    };

    const handleTabValidation = async (values) => {
        const newErrors = {};
        let firstInvalidTab = null; // İlk hatalı sekmeyi tutar

        // Tüm diller ve alanlar için dinamik doğrulama
        const languages = ["tr", "en"];
        const fields = ["title", "summary"];

        languages.forEach((lang, tabIndex) => {
            fields.forEach((field) => {
                if (!values[field][lang]) {
                    if (firstInvalidTab === null) {
                        firstInvalidTab = tabIndex; // İlk hatalı tab'ı belirle
                    }
                }
            });
        });

        // Hatalı bir tab varsa o tab'a yönlendir
        if (firstInvalidTab !== null) {
            setLanguageTabValue(firstInvalidTab);
        }
        return Object.keys(newErrors).length === 0; // Eğer hata yoksa true döner
    };

    const handleSubmit = async (values, { setFieldValue, validateForm }) => {
        // Tüm formu manuel olarak validasyon yap
        const errors = await validateForm();
        if (Object.keys(errors).length > 0) {
            await handleTabValidation(values);
        } else {
            console.log("Form is valid and submitted:", formData);
            await handleNext(values, setFieldValue);
        }
    };

    const handleNext = async (values, setFieldValue) => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        if (activeStep === steps.length - 1) {
            await handleSave(values);
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
    };

    // Age Group options
    const ageGroupOptions = [
        { value: "0-3", label: "0-3 months (Newborn)" },
        { value: "3-6", label: "3-6 months (Early Infant)" },
        { value: "6-12", label: "6-12 months (Mid Infant)" },
        { value: "12-18", label: "12-18 months (Late Infant)" },
        { value: "18-24", label: "18-24 months (Pre-Toddler)" },
        { value: "24-48", label: "2-4 years (Early Childhood)" },
        { value: "48-72", label: "4-6 years (Preschool)" },
        { value: "72-96", label: "6-8 years (Early Primary)" },
        { value: "96-120", label: "8-10 years (Middle Primary)" },
        { value: "120-144", label: "10-12 years (Late Childhood)" }
    ];

    const clearMediaFile = async (setFieldValue) => {
        if (setFieldValue) {
            setFieldValue('mediaUrl', '');
            setFieldValue('duratios', {});
            setFieldValue('fileSizes', {});
        }
    }

    // Helper function to calculate hash from Blob
    const generateHashFromBlob = async (blob) => {
        const arrayBuffer = await blob.arrayBuffer();  // Blob verisini ArrayBuffer'a dönüştür
        const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);  // SHA-256 hash hesapla
        const hashArray = Array.from(new Uint8Array(hashBuffer));  // ArrayBuffer'ı byte dizisine çevir
        const hashHex = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');  // Hex formatına çevir
        return hashHex;  // Hash'i döndür
    };

    const uploadEditorBlobImagesToStorage = async (descriptions) => {
        const updatedDescriptions = { ...descriptions };
        const blobToUrlMap = {};
        const editorUploadIds = []; // tüm editor image uploadIds burada toplanacak

        for (let lang of languages) {
            const editorContent = descriptions[lang.code];

            // editor içeriğinde blob:... ile başlayan image src'lerini yakala
            const blobMatches = [...editorContent.matchAll(/<img.*?src="(blob:[^"]+)".*?>/g)];

            if (blobMatches.length) {
                for (let match of blobMatches) {
                    const blobUrl = match[1]; // ✅ capture group kullanımı
                    const response = await fetch(blobUrl);
                    const blob = await response.blob();
                    const hash = await generateHashFromBlob(blob);

                    if (!blobToUrlMap[hash]) {
                        try {
                            const format = blob.type.split('/')[1];
                            const fileName = `${Date.now()}.${format}`;
                            const file = new File([blob], fileName, { type: blob.type });

                            const uploadResult = await uploadWithTrackingForContentEditor(file, { sourceId: isEditing ? contentId : null });
                            editorUploadIds.push(uploadResult.uploadId); // uploadId ekleniyor
                            blobToUrlMap[hash] = uploadResult.path;
                        } catch (error) {
                            console.error("Blob image upload failed: ", error);
                        }
                    }

                    // blob url’yi gerçek storage url ile değiştir
                    updatedDescriptions[lang.code] =
                        updatedDescriptions[lang.code].replace(blobUrl, blobToUrlMap[hash]);
                }
            }
        }

        return { updatedDescriptions, editorUploadIds };
    };

    // Clear the form.
    const clearForm = () => {
        setIsEditing(false);
        setFormData(defaultFormData);
        setCoverImagePreviewUrl("");
        setStoredCoverImage(null);
        clearMediaFile();
        setActiveStep(0);
        setLanguageTabValue(0);
    }

    const onCloseDialog = () => {
        clearForm();
        onClose();
    }

    const handleBackdropDialog = (event) => {
        event.stopPropagation();
    };

    async function handleUpdate(contentId, values, contentDetails, uploadIds) {
        try {
            const updateContentFn = httpsCallable(functions, "updateContent");

            const result = await updateContentFn({
                contentId,
                title: values.title,
                summary: values.summary,
                author: values.author,
                category: values.category,
                ageGroups: values.ageGroups,
                coverImage: values.coverImage,
                durations: values.durations,
                fileSizes: values.fileSizes,
                contentType: values.contentType,
                state: values.state,
                tags: values.tags,
                contentDetailData: contentDetails,
                uploadIds
            });

            console.log("Content güncellendi:", result.data);
            return result;
        } catch (error) {
            console.error("Update content failed:", error);
            await LogService.addErrorLog("Update content failed", error, {
                step: "handleUpdate",
                contentId,
            });
            throw error; // üst katmanda da yakalansın
        }
    }

    async function handleNewContent(values, contentDetails, uploadIds) {
        try {
            const createContentFn = httpsCallable(functions, "createContent");
            const result = await createContentFn({
                title: values.title,
                summary: values.summary,
                author: values.author,
                category: values.category,
                ageGroups: values.ageGroups,
                coverImage: values.coverImage,
                durations: values.durations,
                fileSizes: values.fileSizes,
                contentType: values.contentType,
                state: values.state,
                tags: values.tags,
                contentDetailData: contentDetails,
                uploadIds
            });

            console.log("Content oluşturuldu, ID:", result.data.contentId);
        } catch (error) {
            console.error("Create content failed:", error);
            await LogService.addErrorLog("Create content failed", error, {
                step: "handleNewContent",
            });
            throw error;
        }
    }

    const handleSave = async (values) => {
        try {
            setLoading(true); // Yükleniyor göstergesini başlat
            let coverImageUrl = storedCoverImage; // varsayılan, update'teysen eski url
            let allUploadIds = [];
            const mediaUploadIds = mediaUploadData?.mediaData?.uploadIds || [];
            allUploadIds.push(...mediaUploadIds);

            if (values.coverImage instanceof File) {
                // Yeni dosya seçilmiş
                try {
                    const uploadResult = await uploadWithTrackingForContentCover(values.coverImage, { sourceId: isEditing ? contentId : null });
                    coverImageUrl = uploadResult.path;
                    allUploadIds.push(uploadResult.uploadId);
                } catch (err) {
                    enqueueSnackbar("Cover image upload failed!", { variant: "error" });
                    await LogService.addErrorLog("Cover image upload failed", err, {
                        step: "handleSave",
                        field: "coverImage",
                    });
                    throw err; // süreci durdursun
                }
            }
            if (values.contentType === "article") {
                let durations = {};
                languages.forEach((lang) => {
                    const readingDurationSeconds = calculateReadingTime(values.description[lang.code]);
                    durations[lang.code] = Math.round(readingDurationSeconds);
                });
                values.durations = durations;
            }

            let descriptions;
            try {
                const { updatedDescriptions, editorUploadIds } = await uploadEditorBlobImagesToStorage(values.description);
                descriptions = updatedDescriptions;
                allUploadIds.push(...editorUploadIds);
            } catch (err) {
                enqueueSnackbar("Some editor images failed to upload!", { variant: "error" });
                await LogService.addErrorLog("Blob image upload failed", err, {
                    step: "uploadEditorBlobImagesToStorage",
                });
                throw err;
            }

            const finalContentData = {
                ...values,
                coverImage: coverImageUrl, //coverImage ekledik
            };

            const contentDetails = languages.map((lang) => ({
                description: descriptions[lang.code],
                mediaUrl: values.contentType === "article" ? "" : values.mediaURLs[lang.code],
                subTitle:
                    values.contentType === "article" || values.contentType === "podcast"
                        ? ""
                        : values.subtitles[lang.code],
                lang: lang.code,
            }));

            if (!isEditing) {//Yeni Kayıt
                handleNewContent(finalContentData, contentDetails, allUploadIds);
                await onContinue();
                enqueueSnackbar("Content saved successfully!", { variant: "success" });
            } else {
                //Güncelleme
                const result = await handleUpdate(contentId, finalContentData, contentDetails, allUploadIds);
                // Base bileşendeki state'i güncelle
                if (onContentUpdated && result.data.success) {
                    onContentUpdated(contentId, result.data.currentContentData);
                }
                // await onContinue();
                enqueueSnackbar("Content updated successfully!", { variant: "success" });
            }
            clearForm();
            onClose();
        } catch (error) {
            console.error("Error saving content: ", error);
            // If save fails
            enqueueSnackbar("Failed to save content!", { variant: "error" });
            await LogService.addErrorLog("Content save failed", error, {
                contentId,
                isEditing,
                step: "handleSave",
            });
        } finally {
            setLoading(false); // Yükleniyor göstergesini durdur
        }
    };

    return (
        <Dialog open={open} onClose={handleBackdropDialog} fullWidth maxWidth="md">
            <DialogTitle>
                {contentType === "article"
                    ? "Article Content"
                    : contentType === "video"
                        ? "Video Content"
                        : contentType === "podcast"
                            ? "Podcast Content"
                            : "Add New Content"}
                <IconButton onClick={onCloseDialog} style={{ position: "absolute", right: 10, top: 10 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Formik
                    initialValues={formData}
                    validationSchema={validationSchema[activeStep]}
                    validationContext={{ coverImageError }}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, touched, errors, setFieldValue, setFieldError, setTouched, handleChange }) => (
                        <Form
                        >
                            <Box width="100%" overflow="auto">
                                <Stepper activeStep={activeStep}>
                                    {steps.map((label) => (
                                        <Step key={label}>
                                            <StepLabel>{label}</StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                                {activeStep === steps.length ? (
                                    <React.Fragment>
                                        <Typography sx={{ mt: 2, mb: 1 }}>
                                            {loading ? "Saving... Please wait." : "All steps completed - you are finished."}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                                            <Box sx={{ flex: '1 1 auto' }} />
                                            {!loading ??
                                                <Button onClick={handleReset}>Reset</Button>
                                            }
                                        </Box>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <Box sx={{ mt: 1, mb: 1 }} minHeight={500}>
                                            {activeStep === 0 && (
                                                <Box>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'stretch', // Yüksekliği eşitler
                                                                }}
                                                            >
                                                                {/* Sol Sütun */}
                                                                <Box sx={{ flex: 1, padding: 2 }}>
                                                                    <Tabs
                                                                        orientation="vertical"
                                                                        variant="scrollable"
                                                                        fullWidth
                                                                        value={languageTabValue}
                                                                        onChange={handleTabChange}
                                                                        aria-label="Vertical tabs example"
                                                                        sx={{ borderRight: 1, borderColor: 'divider' }}
                                                                    >
                                                                        {
                                                                            languages.map((lang, index) => (
                                                                                <Tab label={
                                                                                    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                                                        {lang.label}
                                                                                        {(touched.title?.[lang.code] && errors.title?.[lang.code]) || (touched.summary?.[lang.code] && errors.summary?.[lang.code]) ? <ReportProblemIcon color="error" sx={{ fontSize: '1.3em', marginLeft: "5px" }} /> : null}
                                                                                    </div>
                                                                                } {...a11yProps(0)} />
                                                                            ))
                                                                        }
                                                                    </Tabs>
                                                                </Box>
                                                                {/* Sağ Sütun */}
                                                                <Box sx={{ flex: 11, padding: 2 }}>
                                                                    {
                                                                        languages.map((lang, index) => (
                                                                            <TabPanel value={languageTabValue} index={index}>
                                                                                <Fieldd
                                                                                    as={TextField}
                                                                                    label={`Title (${lang.label})`}
                                                                                    name={`title.${lang.code}`}
                                                                                    fullWidth
                                                                                    value={values.title[lang.code]}
                                                                                    error={touched.title?.[lang.code] && Boolean(errors.title?.[lang.code])}
                                                                                    helperText={touched.title?.[lang.code] && errors.title?.[lang.code]}
                                                                                    errorMessages={touched.title?.[lang.code] && errors.title?.[lang.code]}
                                                                                />
                                                                                <Fieldd
                                                                                    as={TextField}
                                                                                    label={`Summary (${lang.label})`}
                                                                                    name={`summary.${lang.code}`}
                                                                                    multiline
                                                                                    rows={3}
                                                                                    fullWidth
                                                                                    value={values.summary[lang.code]}
                                                                                    error={touched.summary?.[lang.code] && Boolean(errors.summary?.[lang.code])}
                                                                                    helperText={touched.summary?.[lang.code] && errors.summary?.[lang.code]}
                                                                                />
                                                                            </TabPanel>
                                                                        ))
                                                                    }
                                                                </Box>
                                                            </Box>
                                                            <Grid item xs={12}>
                                                                <Grid container spacing={2}>
                                                                    <Grid item xs={12} sm={6}>
                                                                        <Grid item xs={12}>
                                                                            {/* Cover Image Label and Description */}
                                                                            <Box sx={{ mt: 2 }}>
                                                                                <Typography fontSize={14} sx={{ fontWeight: 'bold' }}>Cover Image</Typography>
                                                                                <Typography fontSize={14} color="textSecondary">
                                                                                    Choose a thumbnail that will stand out and grab viewers' attention.
                                                                                </Typography>
                                                                            </Box>
                                                                            <Box display="flex" gap={2}>
                                                                                <Box
                                                                                    sx={{
                                                                                        width: 150,
                                                                                        height: 85,
                                                                                        border: touched.coverImage && errors.coverImage ? "1px dashed red" : "1px dashed gray",
                                                                                        borderRadius: 0,
                                                                                        display: "flex",
                                                                                        alignItems: "center",
                                                                                        justifyContent: "center",
                                                                                        cursor: coverImagePreviewUrl ? "" : "pointer",
                                                                                        position: "relative",
                                                                                        overflow: "hidden",
                                                                                        background: "none",
                                                                                        backgroundSize: 'cover',
                                                                                        backgroundPosition: 'center'
                                                                                    }}
                                                                                >
                                                                                    {coverImagePreviewUrl && (
                                                                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                                                            <img src={coverImagePreviewUrl ? coverImagePreviewUrl : ""} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                                                                                        </Box>
                                                                                    )}
                                                                                    {!coverImagePreviewUrl && !uploading && (
                                                                                        <label htmlFor="file-upload">
                                                                                            <input
                                                                                                type="file"
                                                                                                id="file-upload"
                                                                                                accept=".jpg, .jpeg, .webp"
                                                                                                hidden
                                                                                                onChange={handleFileChange(setFieldValue, setFieldError, setTouched, 'coverImage')}
                                                                                            />
                                                                                            <Box display="flex" flexDirection="column" alignItems="center">
                                                                                                <CloudUploadIcon fontSize="large" />
                                                                                                <Typography fontSize={12}>Upload File</Typography>
                                                                                            </Box>
                                                                                        </label>
                                                                                    )}
                                                                                    {uploading && <CircularProgress size={24} />}
                                                                                </Box>

                                                                                {coverImagePreviewUrl && (
                                                                                    <IconButton onClick={handleRemoveImage(setFieldValue, 'coverImage')}>
                                                                                        <DeleteIcon />
                                                                                    </IconButton>
                                                                                )}
                                                                                {/* Hata mesajını göster */}
                                                                                {touched.coverImage && errors.coverImage && (
                                                                                    <Typography fontSize={12} color="error">
                                                                                        {coverImageError || errors.coverImage}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        </Grid>
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={6}>
                                                                        <Grid item xs={12}>
                                                                            <Grid container spacing={2}>
                                                                                <Grid item xs={12} sm={10}>
                                                                                    <Fieldd name="category">
                                                                                        {({ field }) => (
                                                                                            <FormControl fullWidth error={touched.category && Boolean(errors.category)}>
                                                                                                <Autocomplete
                                                                                                    options={categories}
                                                                                                    getOptionLabel={(option) => option.label}
                                                                                                    value={categories.find((option) => option.value === values.category) || null}
                                                                                                    onChange={(event, newValue) => {
                                                                                                        setFieldValue("category", newValue ? newValue.value : "");
                                                                                                    }}
                                                                                                    renderInput={(params) => (
                                                                                                        <TextField
                                                                                                            {...params}
                                                                                                            label="Select Category"
                                                                                                            error={touched.category && Boolean(errors.category)} // Burada error prop'u eklendi
                                                                                                            helperText={touched.category && errors.category} // Opsiyonel olarak hata mesajı göster
                                                                                                        />
                                                                                                    )}
                                                                                                />
                                                                                            </FormControl>
                                                                                        )}
                                                                                    </Fieldd>
                                                                                </Grid>
                                                                                <Grid item xs={2} container alignItems="center" justifyContent="flex-start">
                                                                                    <IconButton
                                                                                        color="primary"
                                                                                        onClick={loadCategories} // Kategorileri güncellemek için fonksiyon çağrısı
                                                                                        aria-label="refresh"
                                                                                        title="Refresh Categories"
                                                                                    >
                                                                                        <RefreshIcon />
                                                                                    </IconButton>
                                                                                </Grid>
                                                                                <Grid item xs={12} sm={12}>
                                                                                    <Fieldd name="author">
                                                                                        {({ field }) => (
                                                                                            <Autocomplete
                                                                                                options={authors}
                                                                                                getOptionLabel={(option) => option.label}
                                                                                                value={authors.find((option) => option.value === values.author) || null}
                                                                                                onChange={(event, newValue) => {
                                                                                                    setFieldValue("author", newValue ? newValue.value : ""); // Seçilen değeri kaydet
                                                                                                }}
                                                                                                renderInput={(params) => <TextField {...params} label="Select Author" />}
                                                                                            />
                                                                                        )}
                                                                                    </Fieldd>
                                                                                </Grid>
                                                                                <Grid item xs={12} sm={12}>
                                                                                    <Fieldd name="ageGroups"
                                                                                        label="Age Group">
                                                                                        {({ field }) => (
                                                                                            <Autocomplete
                                                                                                multiple
                                                                                                id="checkboxes-tags-demo"
                                                                                                options={ageGroupOptions}
                                                                                                disableCloseOnSelect
                                                                                                value={ageGroupOptions.filter(option =>
                                                                                                    values.ageGroups?.includes(option.value)
                                                                                                )}
                                                                                                onChange={(event, newValue) => {
                                                                                                    const selectedValues = newValue.map(item => item.value); // sadece value değerlerini almak
                                                                                                    setFieldValue("ageGroups", selectedValues); // Formik'e selected value array'ini kaydediyoruz
                                                                                                }}
                                                                                                renderOption={(props, option, { selected }) => {
                                                                                                    const { key, ...optionProps } = props;
                                                                                                    return (
                                                                                                        <li key={key} {...optionProps}>
                                                                                                            <Checkbox
                                                                                                                icon={icon}
                                                                                                                checkedIcon={checkedIcon}
                                                                                                                style={{ marginRight: 8 }}
                                                                                                                checked={selected}
                                                                                                            />
                                                                                                            {option.label}
                                                                                                        </li>
                                                                                                    );
                                                                                                }}
                                                                                                renderInput={(params) => (
                                                                                                    <TextField {...params} label="Select Age Group" />
                                                                                                )}
                                                                                            />
                                                                                        )}
                                                                                    </Fieldd>
                                                                                </Grid>
                                                                                <Grid item xs={12} sm={12}>
                                                                                    <ChipsInput
                                                                                        chips={values.tags}
                                                                                        setChips={handleCustomChange(setFieldValue, 'tags')}
                                                                                        label="Tags"
                                                                                        error={touched.tags && Boolean(errors.tags)}
                                                                                    />
                                                                                    {errors.tags && touched.tags && (
                                                                                        <FormHelperText error>{errors.tags}</FormHelperText>
                                                                                    )}
                                                                                </Grid>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            )}

                                            {activeStep === 1 && (
                                                <Box>
                                                    <MultilingualRichTextEditor
                                                        touched={touched}
                                                        errors={errors}
                                                        languages={languages}
                                                        description={values.description}
                                                        setFieldValue={setFieldValue}
                                                        onChange={handleDescriptionChange(setFieldValue, 'description')}
                                                        a11yProps={a11yProps}
                                                    />
                                                </Box>
                                            )}
                                            {/* {activeStep === 2 && (
                                                <Box>
                                                    <Typography>Title: {values.title.en}</Typography>
                                                    <Typography>Summary: {values.summary.en}</Typography>
                                                </Box>
                                            )} */}

                                            {activeStep === (isMediaType ? 2 : 999) && isMediaType && (
                                                <Box sx={{ mt: 2 }}>
                                                    <MediaPreviewAndSubtitle
                                                        values={values}
                                                        setFieldValue={setFieldValue}
                                                        loading={loading}
                                                    />
                                                </Box>
                                            )}

                                            {activeStep === (isMediaType ? 3 : 2) && (
                                                <Box sx={{ mt: 3 }}>
                                                    <Typography variant="h6" gutterBottom>
                                                        Review & Approval
                                                    </Typography>

                                                    {/* Dil Sekmeleri */}
                                                    <Tabs
                                                        value={activeLang}
                                                        onChange={(e, val) => setActiveLang(val)}
                                                        textColor="primary"
                                                        indicatorColor="primary"
                                                        sx={{ mb: 2 }}
                                                    >
                                                        <Tab label="TR" value="tr" />
                                                        <Tab label="EN" value="en" />
                                                    </Tabs>

                                                    {/* Mobil Önizleme Alanı */}
                                                    <Box
                                                        sx={{
                                                            width: 400,
                                                            mx: "auto",
                                                            borderRadius: 3,
                                                            overflow: "hidden",
                                                            boxShadow: 3,
                                                            bgcolor: "background.paper",
                                                            mb: 4,
                                                        }}
                                                    >
                                                        {/* Kapak Görseli */}
                                                        <Box sx={{ position: "relative" }}>
                                                            <Box
                                                                component="img"
                                                                src={coverImagePreviewUrl}
                                                                alt="Cover"
                                                                sx={{
                                                                    width: "100%",
                                                                    height: 200,
                                                                    objectFit: "cover",
                                                                }}
                                                            />
                                                            <Chip
                                                                label={
                                                                    categories.find(cat => cat.value === values.category)?.title?.[activeLang]
                                                                    || "-"
                                                                }
                                                                color="primary"
                                                                size="small"
                                                                sx={{ position: "absolute", bottom: 8, left: 8 }}
                                                            />
                                                        </Box>

                                                        {/* İçerik Bilgileri */}
                                                        <Box sx={{ p: 2 }}>
                                                            <Typography
                                                                variant="h6"
                                                                sx={{ mb: 1, fontWeight: 600 }}
                                                            >
                                                                {values.title?.[activeLang] || "—"}
                                                            </Typography>

                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{ mb: 2 }}
                                                            >
                                                                {values.summary?.[activeLang] || "—"}
                                                            </Typography>

                                                            <Divider sx={{ mb: 2 }} />

                                                            <Typography
                                                                variant="body2"
                                                                dangerouslySetInnerHTML={{
                                                                    __html:
                                                                        values.description?.[activeLang] ||
                                                                        "<i>No description available.</i>",
                                                                }}
                                                            />

                                                            {/* Ek Bilgiler */}
                                                            <Box sx={{ mt: 3 }}>
                                                                <Typography variant="body2">
                                                                    <strong>Author:</strong> {values.author || "-"}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    <strong>Age Range:</strong>{" "}
                                                                    {values.ageGroups?.length ? values.ageGroups.join(", ") : "-"}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    <strong>Tags:</strong>{" "}
                                                                    {values.tags?.length ? values.tags.join(", ") : "-"}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>

                                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                                            <Button
                                                variant="contained" color={activeStep === 0 ? 'inherit' : 'primary'}
                                                disabled={activeStep === 0}
                                                onClick={handleBack}
                                                sx={{ mr: 1 }}
                                            >
                                                Back
                                            </Button>
                                            <Box sx={{ flex: '1 1 auto' }} />
                                            <Button type="submit" variant="contained" color={activeStep === steps.length - 1 ? 'success' : 'primary'}>
                                                {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                                            </Button>
                                        </Box>
                                    </React.Fragment>
                                )}
                                {loading && (
                                    <div style={{
                                        position: 'fixed',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        zIndex: 1000, // Diğer öğelerin üstünde olmasını sağlar
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)', // Hafif opak bir arka plan
                                        padding: '20px',
                                        borderRadius: '8px',
                                    }}>
                                        <CircularProgress />
                                    </div>
                                )}
                            </Box>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
            <DialogActions>
            </DialogActions>
        </Dialog >
    );
}